/**
 * navigation/surfaceRouter.tsx
 * SK Queue navigation is surface-based, not a single tabbed god-view: each
 * surface is its own entry point gated by persona ∩ entitlements. This tiny
 * router holds the active surface, keeps it in sync with the URL (?surface=…)
 * on web so surfaces are deep-linkable/shareable, and auto-corrects to a valid
 * surface whenever the persona/entitlements change (mirrors the prototype's
 * auto-jump). Public surfaces (guest, board) render standalone; the rest live
 * in the admin shell — that split is decided in rootNavigator.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { useSession } from '../context/sessionContext';
import { visibleSurfaces, getSurface, type SurfaceKey } from '../queue/surfaces';

interface SurfaceRouterValue {
  surface: SurfaceKey;
  setSurface: (s: SurfaceKey) => void;
  isPublic: boolean;
}

const SurfaceRouterContext = createContext<SurfaceRouterValue | null>(null);

const VALID = new Set<SurfaceKey>(['dashboard', 'detail', 'guest', 'board', 'host', 'voice', 'chat']);

// Legacy surface keys that were merged/renamed → their new home (rev 5). The
// ex-admin screens (Operator/Config/Live View) are now sub-tabs of the single
// Queue Detail surface; old links land on the engine Dashboard. Keeps old QR
// codes / bookmarks working.
const SURFACE_ALIASES: Record<string, SurfaceKey> = {
  operator: 'detail',
  config: 'detail',
  settings: 'detail',
  view: 'detail',
};

function readUrlSurface(): SurfaceKey | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('surface');
  if (!raw) return null;
  const p = (SURFACE_ALIASES[raw] ?? raw) as SurfaceKey;
  return VALID.has(p) ? p : null;
}

function writeUrlSurface(s: SurfaceKey): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('surface', s);
  window.history.replaceState({}, '', url.toString());
}

export function SurfaceRouterProvider({ children }: { children: React.ReactNode }) {
  const { accessLevel, entitlements, isAuthenticated, loading } = useSession();

  const [surface, setSurfaceState] = useState<SurfaceKey>(() => {
    // Honor an explicit ?surface= from the URL on first load, even if it's an
    // admin surface the (not-yet-probed) session can't see — rootNavigator will
    // redirect an unauthenticated visitor to the platform login for it.
    const fromUrl = readUrlSurface();
    if (fromUrl) return fromUrl;
    // No explicit surface = the bare product root. Default to the engine home
    // (Dashboard) — an anonymous visitor at "/" is sent to login, matching every
    // other SK product. Product surfaces (Guest QR, Now Serving) are only reached
    // via their own explicit ?surface= URLs (previews).
    return 'dashboard';
  });

  const setSurface = useCallback((s: SurfaceKey) => {
    setSurfaceState(s);
    writeUrlSurface(s);
  }, []);

  // Auto-correct when the active surface becomes invalid for an AUTHENTICATED
  // user (persona/entitlement change — the prototype's auto-jump). For an
  // unauthenticated visitor we do NOT downgrade an admin surface to a public
  // one: rootNavigator instead redirects them to the platform login for it.
  // (We still wait for the session probe to finish before correcting.)
  useEffect(() => {
    if (loading) return;
    const target = getSurface(surface);
    // Public surfaces (Guest QR, Now Serving display) are standalone and
    // persona-independent — a guest scanning the QR or a door TV must render
    // regardless of any admin session. Never auto-correct away from one, even
    // when an admin is logged in (their persona doesn't "allow" guest, but the
    // explicit public URL must still win). [Was redirecting guest → operator.]
    if (target.public) return;
    if (!isAuthenticated && !target.public) return; // let rootNavigator redirect
    // `detail` is a valid engine surface but only meaningful with a selected
    // queue; treat it as allowed here (the Detail screen handles "no queue").
    const allowed = [...visibleSurfaces(accessLevel, entitlements)];
    if (!allowed.includes(surface)) {
      const next = allowed[0] ?? 'dashboard';
      setSurfaceState(next);
      writeUrlSurface(next);
    }
  }, [accessLevel, entitlements, surface, isAuthenticated, loading]);

  const value: SurfaceRouterValue = {
    surface,
    setSurface,
    isPublic: getSurface(surface).public,
  };

  return <SurfaceRouterContext.Provider value={value}>{children}</SurfaceRouterContext.Provider>;
}

export function useSurfaceRouter(): SurfaceRouterValue {
  const ctx = useContext(SurfaceRouterContext);
  if (!ctx) throw new Error('useSurfaceRouter must be used within SurfaceRouterProvider');
  return ctx;
}
